// Constants
import { AssociationType, ClientAppId } from "@ms/uno-constants/lib/local/AppConstants";
import { ContextDisplayType } from "../../../service/graph/general/ContextDisplayType";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import cloneDeep from "lodash/cloneDeep";
import { stringToEnum } from "@ms/uno-utilities/lib/local/EnumUtilities";
/**
 * The plan context link entity implementation.
 */ export class PlanContextLink extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.displayNameSegments]: changed.displayNameSegments,
            [resourceKeyMap.webUrl]: changed.webUrl,
            [resourceKeyMap.associationType]: changed.associationType,
            [resourceKeyMap.displayAs]: changed.displayAs,
            [resourceKeyMap.isActive]: changed.isActive,
            [resourceKeyMap.customLinkText]: changed.customLinkText
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            associationType: (deltaValue)=>deltaValue ? stringToEnum(deltaValue, AssociationType) ?? AssociationType.UnknownValue : null,
            displayAs: (deltaValue)=>deltaValue ? stringToEnum(deltaValue, ContextDisplayType) ?? ContextDisplayType.Other : null,
            createdDateTime: (deltaValue)=>Temporal.Instant.from(deltaValue)
        });
    }
    static fromResource(resource) {
        return new PlanContextLink({
            id: resource.id,
            version: resource["@etag"],
            key: resource.key,
            displayNameSegments: resource.displayNameSegments,
            webUrl: resource.webUrl,
            associationType: resource.associationType ? stringToEnum(resource.associationType, AssociationType) ?? AssociationType.UnknownValue : null,
            displayAs: resource.displayAs ? stringToEnum(resource.displayAs, ContextDisplayType) ?? ContextDisplayType.Other : null,
            isActive: resource.isActive,
            customLinkText: resource.customLinkText,
            ownerAppId: resource.ownerAppId,
            isCreation: resource.isCreation,
            createdDateTime: Temporal.Instant.from(resource.createdDateTime)
        });
    }
    isVisible() {
        return this.webUrl !== "" && this.displayAs !== null;
    }
    getClone() {
        return new PlanContextLink({
            id: this.id,
            version: this.version,
            key: this.key,
            displayNameSegments: cloneDeep(this.displayNameSegments),
            webUrl: this.webUrl,
            associationType: this.associationType,
            displayAs: this.displayAs,
            isActive: this.isActive,
            customLinkText: this.customLinkText,
            ownerAppId: this.ownerAppId,
            isCreation: this.isCreation,
            createdDateTime: Temporal.Instant.from(this.createdDateTime)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            key: this.key,
            displayNameSegments: this.displayNameSegments,
            webUrl: this.webUrl,
            associationType: this.associationType,
            displayAs: this.displayAs,
            isActive: this.isActive,
            customLinkText: this.customLinkText,
            ownerAppId: this.ownerAppId,
            isCreation: this.isCreation,
            createdDateTime: this.createdDateTime.toString()
        };
    }
    forCreate() {
        return {
            key: this.key,
            displayNameSegments: this.displayNameSegments,
            webUrl: this.webUrl,
            associationType: this.associationType,
            displayAs: this.displayAs,
            isActive: this.isActive,
            customLinkText: this.customLinkText
        };
    }
    constructor(propertyBag){
        super(propertyBag);
        this.key = propertyBag.key;
        this.displayNameSegments = propertyBag.displayNameSegments;
        this.webUrl = propertyBag.webUrl;
        this.associationType = propertyBag.associationType;
        this.displayAs = propertyBag.displayAs;
        this.isActive = propertyBag.isActive;
        this.customLinkText = propertyBag.customLinkText ?? null;
        this.ownerAppId = propertyBag.ownerAppId ?? ClientAppId.MicrosoftPlannerClient;
        this.isCreation = propertyBag.isCreation ?? false;
        this.createdDateTime = propertyBag.createdDateTime ?? Temporal.Now.instant();
    }
}
